import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckIcon,
  Check as CheckSmallIcon,
  Close as CloseIcon,
  Autorenew as RefreshIcon,
} from '@mui/icons-material';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { generateCompanyName, validateCompanyName, validateEmail } from '../utils/companyNameGenerator';
import { useSystemConfig } from '../hooks/useSystemConfig';

// Validation state types
interface ValidationState {
  email: { checking: boolean; available: boolean | null; message: string };
  companyName: { checking: boolean; available: boolean | null; message: string };
}

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signups_open: signupsOpen, signups_closed_message: signupsClosedMessage } = useSystemConfig();
  const [searchParams] = useSearchParams();
  const emailFromQuery = (searchParams.get('email') || '').trim();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: emailFromQuery,
    password: '',
    confirmPassword: '',
    fullName: '',
    companyName: '',
  });

  const [validation, setValidation] = useState<ValidationState>({
    email: { checking: false, available: null, message: '' },
    companyName: { checking: false, available: null, message: '' },
  });

  // Check email availability
  const checkEmailAvailability = async (email: string) => {
    if (!email) {
      setValidation((prev) => ({
        ...prev,
        email: { checking: false, available: null, message: '' },
      }));
      return;
    }

    // Validate email format first
    const formatValidation = validateEmail(email);
    if (!formatValidation.valid) {
      setValidation((prev) => ({
        ...prev,
        email: { checking: false, available: false, message: formatValidation.error || '' },
      }));
      return;
    }

    setValidation((prev) => ({
      ...prev,
      email: { checking: true, available: null, message: 'Checking...' },
    }));

    try {
      const response = await fetch(`/api/v2/customers/check-email/${encodeURIComponent(email)}`);
      const data = await response.json();
      
      setValidation((prev) => ({
        ...prev,
        email: {
          checking: false,
          available: data.data?.available || false,
          message: data.data?.message || '',
        },
      }));
    } catch (err) {

      setValidation((prev) => ({
        ...prev,
        email: { checking: false, available: false, message: 'Error checking availability' },
      }));
    }
  };

  useEffect(() => {
    if (!emailFromQuery) {
      return;
    }
    setFormData((prev) => {
      if (prev.email) {
        return prev;
      }
      return { ...prev, email: emailFromQuery };
    });
    checkEmailAvailability(emailFromQuery);
  }, [emailFromQuery]);

  // Check company name availability
  const checkCompanyAvailability = async (companyName: string) => {
    if (!companyName) {
      setValidation((prev) => ({
        ...prev,
        companyName: { checking: false, available: null, message: '' },
      }));
      return;
    }

    // Validate company name format first
    const formatValidation = validateCompanyName(companyName);
    if (!formatValidation.valid) {
      setValidation((prev) => ({
        ...prev,
        companyName: { checking: false, available: false, message: formatValidation.error || '' },
      }));
      return;
    }

    setValidation((prev) => ({
      ...prev,
      companyName: { checking: true, available: null, message: 'Checking...' },
    }));

    try {
      const response = await fetch(`/api/v2/customers/check-company/${encodeURIComponent(companyName)}`);
      const data = await response.json();
      
      setValidation((prev) => ({
        ...prev,
        companyName: {
          checking: false,
          available: data.data?.available || false,
          message: data.data?.message || '',
        },
      }));
    } catch (err) {

      setValidation((prev) => ({
        ...prev,
        companyName: { checking: false, available: false, message: 'Error checking availability' },
      }));
    }
  };

  // Generate and check random company name
  const handleGenerateRandom = async () => {
    const randomName = generateCompanyName();
    setFormData((prev) => ({ ...prev, companyName: randomName }));
    await checkCompanyAvailability(randomName);
  };

  // Handle email blur
  const handleEmailBlur = () => {
    checkEmailAvailability(formData.email);
  };

  // Handle company name blur
  const handleCompanyNameBlur = () => {
    checkCompanyAvailability(formData.companyName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!signupsOpen) return;

    // Validate form
    if (!formData.email || !formData.password || !formData.fullName || 
        !formData.companyName) {
      setError('Please fill in all fields');
      return;
    }

    // Check that email and company name are available
    if (validation.email.available !== true) {
      setError('Please verify your email is available');
      return;
    }

    if (validation.companyName.available !== true) {
      setError('Please verify your company code is available');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Create Firebase user
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Call API to create customer
      const response = await fetch('/api/v2/customers/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await userCredential.user.getIdToken()}`
        },
        body: JSON.stringify({
          email: formData.email,
          full_name: formData.fullName,
          company_name: formData.companyName,
          company_display_name: formData.companyName,
          firebase_uid: userCredential.user.uid,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create customer');
      }

      setSuccess(true);
      
      // Wait a bit for backend to complete processing
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force refresh the auth token to ensure proper validation
      await userCredential.user.getIdToken(true);
      
      // Redirect to dashboard after showing success
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (err: any) {

      
      // Handle specific Firebase errors
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError(err.message || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <Card sx={{ maxWidth: 400, width: '100%', m: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Account Created Successfully!
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Redirecting to your dashboard...
            </Typography>
            <CircularProgress sx={{ mt: 3 }} size={24} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      p: 2
    }}>
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Create Your Account
          </Typography>
          <Typography variant="body1" align="center" color="textSecondary" gutterBottom>
            Start building AI-powered applications today
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          {!signupsOpen ? (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info">{signupsClosedMessage}</Alert>
              <Typography variant="body2" align="center" sx={{ mt: 3 }}>
                Already have an account?{' '}
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  Sign in
                </Link>
              </Typography>
            </Box>
          ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Full Name"
              margin="normal"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              required
              autoFocus
            />

            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                margin="normal"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                onBlur={handleEmailBlur}
                required
                error={validation.email.available === false}
                helperText={validation.email.message}
                InputProps={{
                  endAdornment: validation.email.checking ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : validation.email.available === true ? (
                    <CheckSmallIcon sx={{ color: 'success.main', mr: 1 }} />
                  ) : validation.email.available === false ? (
                    <CloseIcon sx={{ color: 'error.main', mr: 1 }} />
                  ) : null,
                }}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 4, py: 1, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
              <Typography variant="body2">
                💡 Your <strong>Company Code</strong> will be used in your API routes. Choose something memorable!
              </Typography>
            </Alert>
            <Button
                variant="outlined"
                size="small"
                onClick={handleGenerateRandom}
                disabled={validation.companyName.checking}
                sx={{ mt: 1 }}
                startIcon={<RefreshIcon />}
              >
                Generate Random
              </Button>
            <Box sx={{ position: 'relative', pb: 4}}>
              <TextField
                fullWidth
                label="Company Code"
                margin="normal"
                value={formData.companyName}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                  setFormData({ ...formData, companyName: value });
                }}
                onBlur={handleCompanyNameBlur}
                required
                helperText={validation.companyName.message || '8-30 characters, lowercase, numbers, and dashes'}
                slotProps={{ htmlInput: { maxLength: 30 } }}
                error={validation.companyName.available === false}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {validation.companyName.checking ? (
                        <CircularProgress size={20} />
                      ) : validation.companyName.available === true ? (
                        <CheckSmallIcon sx={{ color: 'success.main' }} />
                      ) : validation.companyName.available === false ? (
                        <CloseIcon sx={{ color: 'error.main' }} />
                      ) : null}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
              helperText={
                formData.confirmPassword !== '' && formData.password !== formData.confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || validation.email.available !== true || validation.companyName.available !== true}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>

            <Divider sx={{ my: 2 }}>OR</Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  Sign in
                </Link>
              </Typography>
            </Box>
          </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
